import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapContainer, TileLayer, Circle, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Layout from "@/components/Layout";
import { useUser } from "@/context/UserContext";
import api from "@/utils/api";
import { useNavigate } from "react-router-dom";
import { Clock, CheckCircle, XCircle, MessageCircle, User, FileText, MapPin, RefreshCw, LocateFixed, Map as MapIcon, List } from "lucide-react";
import useFadeEdges from "@/hooks/useFadeEdges";
import InviteCoInterpreterModal from "@/components/InviteCoInterpreterModal";
import { geocodeBatch, haversineKm } from "@/utils/geocode";

// Fix leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const STATUS_CONFIG = {
  pending: { label: "Anfrage", color: "text-amber-600 bg-amber-50 border-amber-200" },
  accepted: { label: "Angenommen", color: "text-green-600 bg-green-50 border-green-200" },
  rejected: { label: "Abgelehnt", color: "text-red-600 bg-red-50 border-red-200" },
  withdrawn: { label: "Zurückgezogen", color: "text-gray-500 bg-gray-100 border-gray-200" },
  completed: { label: "Erledigt", color: "text-gray-600 bg-gray-100 border-gray-300" },
};

// A request counts as "Erledigt" once it was accepted AND its end date+time has passed.
const isCompletedRequest = (req) => {
  if (req.status !== "accepted") return false;
  const lastDate = req.event_date_to || req.event_date;
  if (!lastDate) return false;
  const endTime = req.event_time_end || "23:59";
  // Construct end as local datetime; treat absence of time as end-of-day
  const end = new Date(`${lastDate}T${endTime}:00`);
  if (isNaN(end.getTime())) return false;
  return end.getTime() < Date.now();
};

const MONTHS_DE = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
const formatGermanDate = (isoDate) => {
  if (!isoDate || !/^\d{4}-\d{2}-\d{2}/.test(isoDate)) return isoDate || "";
  const [y, m, d] = isoDate.split("-");
  return `${parseInt(d, 10)}. ${MONTHS_DE[parseInt(m, 10) - 1]} ${y}`;
};

// Distance helpers moved to /utils/geocode.js

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    const lat = Number(target.lat);
    const lng = Number(target.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    map.flyTo([lat, lng], target.zoom || 11, { duration: 1.0 });
  }, [target, map]);
  return null;
}

function ClientProfileModal({ deafUserId, onClose }) {
  const [profile, setProfile] = useState(null);
  const [approvals, setApprovals] = useState([]);
  useEffect(() => {
    api.get(`/client-profiles/${deafUserId}`).then((r) => setProfile(r.data)).catch(() => {});
    api.get(`/cost-approvals/${deafUserId}`).then((r) => setApprovals(r.data)).catch(() => {});
  }, [deafUserId]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}>
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 text-lg">Profil des Kunden</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">×</button>
        </div>
        {profile ? (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#FEF3CE] rounded-xl flex items-center justify-center text-xl">👂</div>
              <div>
                <p className="font-bold text-gray-900">{profile.name}</p>
                <p className="text-sm text-gray-500">{profile.email}</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2 text-sm">
                <FileText size={16} className="text-[#CA8A04]" />
                Kostenzusagen ({approvals.length})
              </h4>
              {approvals.length === 0 ? (
                <p className="text-sm text-gray-400">Keine Kostenzusagen hochgeladen</p>
              ) : (
                <div className="space-y-2">
                  {approvals.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 p-2 bg-amber-50 rounded-xl text-sm">
                      <FileText size={14} className="text-[#CA8A04]" />
                      <span className="flex-1 truncate">{a.filename}</span>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} target="_blank" rel="noreferrer"
                        className="text-[#0D9488] hover:underline text-xs">Öffnen</a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <p className="text-gray-400 text-sm">Wird geladen...</p>
        )}
      </motion.div>
    </motion.div>
  );
}

// Modern minimalistic location marker: white ring + solid color circle + center dot.
// inside=true → teal with subtle pulse animation; outside=false → amber-yellow (no pulse).
// `scale` grows the icon by 3% per zoom level above 14 (Maßstab 1:1km).
const makeRequestIcon = (inside, scale = 1) => {
  const color = inside ? "#14B8A6" : "#F59E0B";
  const baseSize = 26;
  const size = Math.round(baseSize * scale);
  const pulseClass = inside ? "if-marker-pulse" : "";
  return L.divIcon({
    html: `<div class="${pulseClass}" style="width:${size}px;height:${size}px;position:relative">
        <svg width="${size}" height="${size}" viewBox="0 0 26 26" xmlns="http://www.w3.org/2000/svg" style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.25));position:relative;z-index:1">
          <circle cx="13" cy="13" r="11" fill="white"/>
          <circle cx="13" cy="13" r="9" fill="${color}"/>
          <circle cx="13" cy="13" r="3" fill="white"/>
        </svg>
      </div>`,
    className: "",
    iconSize: [size, size],
    iconAnchor: [Math.round(size / 2), Math.round(size / 2)],
    popupAnchor: [0, -Math.round(size / 2) - 2],
  });
};

// Subscribes to zoomend and reports current zoom to parent via setZoom
function ZoomWatcher({ onZoom }) {
  const map = useMapEvents({
    zoomend: () => onZoom(map.getZoom()),
  });
  useEffect(() => { onZoom(map.getZoom()); }, []); // initial
  return null;
}

export default function InterpreterDashboard() {
  const { user } = useUser();
  const navigate = useNavigate();
  const { ref: tabsFadeRef, edgeClass: tabsFadeClass } = useFadeEdges();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeaf, setSelectedDeaf] = useState(null);
  const [filter, setFilter] = useState("pending");
  // Mobile: 'list' oder 'map' — auf Desktop irrelevant (CSS-Breakpoint zeigt beide)
  const [mobileView, setMobileView] = useState("list");
  // Track viewport breakpoint (Tailwind md = 768px) — used to conditionally mount the map
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" ? window.innerWidth >= 768 : true);
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const shouldShowMap = isDesktop || mobileView === "map";
  // Speichert Zeitpunkt, ab dem Anfragen als "neu" markiert werden (eingefroren beim Öffnen des Anfragen-Tabs).
  // Persistiert in localStorage je User. Wird aktualisiert, wenn der Nutzer den Anfragen-Tab verlässt.
  const lsKey = `if_last_seen_anfragen_${user.id}`;
  const initialSeenRef = useRef(null);
  if (initialSeenRef.current === null) {
    // Beim ersten Mount: aktuellen gespeicherten Wert einfrieren (für Badge-Anzeige).
    initialSeenRef.current = parseInt(localStorage.getItem(lsKey) || "0", 10);
  }
  const [previousFilter, setPreviousFilter] = useState("pending");
  useEffect(() => {
    // Wenn der Nutzer den Anfragen-Tab gerade verlässt → "gesehen"-Timestamp jetzt speichern.
    if (previousFilter === "pending" && filter !== "pending") {
      const now = Date.now();
      localStorage.setItem(lsKey, String(now));
      initialSeenRef.current = now;
    }
    setPreviousFilter(filter);
  }, [filter, lsKey, previousFilter]);

  const isNewRequest = (req) => {
    if (req.status !== "pending") return false;
    if (!req.created_at) return false;
    const created = new Date(req.created_at).getTime();
    return created > (initialSeenRef.current || 0);
  };
  const newCount = requests.filter(isNewRequest).length;  const [profile, setProfile] = useState(null);
  const [geo, setGeo] = useState({});       // { address -> {lat,lng}|null }
  const [flyTarget, setFlyTarget] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [userLocation, setUserLocation] = useState(null); // GPS-Position des Nutzers
  const [locating, setLocating] = useState(false);
  const [mapZoom, setMapZoom] = useState(11);
  const [inviteCoFor, setInviteCoFor] = useState(null); // Request-Objekt, für das Co eingeladen wird
  const geocodingRef = useRef(false);

  // Skalierung der Marker: ab Zoom 14 (≈ 1:1km) wächst Marker je Zoom-Stufe um 3 %
  const markerScale = Math.max(1, 1 + 0.03 * Math.max(0, mapZoom - 14));

  // GPS-Position beim ersten Laden ermitteln (asynchron, ohne Permission-Wand zu blockieren)
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = Number(pos?.coords?.latitude);
        const lng = Number(pos?.coords?.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setUserLocation({ lat, lng });
        }
      },
      () => { /* Permission denied oder kein Signal — wir nutzen Profil-Fallback */ },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  }, []);

  const recenterToMe = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = Number(pos?.coords?.latitude);
        const lng = Number(pos?.coords?.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          const loc = { lat, lng };
          setUserLocation(loc);
          setFlyTarget({ ...loc, zoom: 13 }); // 1:~6km bei GPS-Zentrierung
        } else if (profile?.service_center_lat) {
          setFlyTarget({ lat: profile.service_center_lat, lng: profile.service_center_lng, zoom: 11 });
        }
        setLocating(false);
      },
      () => {
        // Fallback: zum Profil-Zentrum, falls GPS verweigert
        if (profile?.service_center_lat) {
          setFlyTarget({ lat: profile.service_center_lat, lng: profile.service_center_lng, zoom: 11 });
        }
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Initial-Center: einmal auf GPS oder Profil-Zentrum fliegen (sobald verfügbar)
  const didInitialCenter = useRef(false);
  useEffect(() => {
    if (didInitialCenter.current) return;
    if (userLocation) {
      setFlyTarget({ ...userLocation, zoom: 11 }); // 1:~15km Standard
      didInitialCenter.current = true;
    } else if (Number.isFinite(profile?.service_center_lat) && Number.isFinite(profile?.service_center_lng)) {
      setFlyTarget({ lat: profile.service_center_lat, lng: profile.service_center_lng, zoom: 11 });
      didInitialCenter.current = true;
    }
  }, [userLocation, profile]);

  const loadRequests = useCallback(() => {
    api.get(`/requests?user_id=${user.id}&role=interpreter`)
      .then((r) => setRequests(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.id]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  // Poll for new requests every 15s (lightweight — only when tab is visible)
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") loadRequests();
    }, 15000);
    return () => clearInterval(interval);
  }, [loadRequests]);

  // Track which request IDs just arrived → trigger slide-in animation
  const prevIdsRef = useRef(null);
  const [slidingInIds, setSlidingInIds] = useState(new Set());
  useEffect(() => {
    const currentIds = new Set(requests.map((r) => r.id));
    if (prevIdsRef.current === null) {
      // First render — no animation
      prevIdsRef.current = currentIds;
      return;
    }
    const fresh = [];
    currentIds.forEach((id) => { if (!prevIdsRef.current.has(id)) fresh.push(id); });
    prevIdsRef.current = currentIds;
    if (fresh.length > 0) {
      setSlidingInIds((prev) => new Set([...prev, ...fresh]));
      // Clear flag after animation completes (700 ms)
      setTimeout(() => {
        setSlidingInIds((prev) => {
          const next = new Set(prev);
          fresh.forEach((id) => next.delete(id));
          return next;
        });
      }, 800);
    }
  }, [requests]);

  // Load own interpreter profile (for service-center lat/lng/radius)
  useEffect(() => {
    api.get(`/interpreters/${user.id}`)
      .then((r) => setProfile(r.data))
      .catch(() => {});
  }, [user.id]);

  // Geocode all unique event_locations across requests, sequentially.
  useEffect(() => {
    if (geocodingRef.current) return;
    const addrs = Array.from(new Set(
      requests.map((r) => r.event_location).filter(Boolean)
    ));
    if (addrs.length === 0) return;
    geocodingRef.current = true;
    geocodeBatch(addrs, (addr, res) => {
      setGeo((prev) => ({ ...prev, [addr]: res }));
    }).finally(() => { geocodingRef.current = false; });
  }, [requests]);

  const handleApplyBroadcast = async (requestId) => {
    try {
      const r = await api.post(`/requests/${requestId}/apply?interpreter_id=${user.id}`);
      setRequests((prev) => prev.map((req) => req.id === requestId ? { ...req, already_applied: true, applicants: [...(req.applicants||[]), { interpreter_id: user.id, name: user.name }] } : req));
      alert(`Bewerbung gesendet! ${r.data.applicants_count} Bewerbung${r.data.applicants_count!==1?'en':''} bisher.`);
    } catch (e) {
      if (e.response?.status === 409 || e.response?.status === 410) {
        alert(e.response?.data?.detail || "Anfrage nicht mehr verfügbar");
        loadRequests();
      } else {
        alert(e.response?.data?.detail || "Bewerbung fehlgeschlagen");
      }
    }
  };

  const handleStatus = async (requestId, status) => {
    try {
      await api.put(`/requests/${requestId}/status`, { status });
      setRequests((prev) => prev.map((r) => r.id === requestId ? { ...r, status } : r));
    } catch (e) {
      if (e.response?.status === 409) {
        // Race-Conflict: andere Seite (IO oder paralleler Klick) war schneller
        alert("Diese Anfrage wurde bereits entschieden — vermutlich hast du sie schon in Interpreter's Office angenommen/abgelehnt. Aktualisiere die Liste.");
        loadRequests();
      } else {
        alert(e.response?.data?.detail || "Fehler beim Aktualisieren");
      }
    }
  };

  const handleCoStatus = async (requestId, status) => {
    try {
      const r = await api.put(`/requests/${requestId}/co-status`, {
        status, co_interpreter_id: user.id,
      });
      setRequests((prev) => prev.map((req) => req.id === requestId ? { ...req, ...r.data } : req));
    } catch (e) {
      alert(e.response?.data?.detail || "Fehler bei Co-Entscheidung");
    }
  };

  const handleResendWebhook = async (requestId) => {
    try {
      const r = await api.post(`/requests/${requestId}/resend-io-webhook`);
      alert(`Webhook erneut gesendet (${r.data.sent_status}).\nStatus: ${r.data.last_webhook_status}${r.data.error ? "\nFehler: " + r.data.error : ""}`);
    } catch (e) {
      alert("Fehler beim Senden: " + (e.response?.data?.detail || e.message));
    }
  };

  const filtered = (() => {
    let list = [];
    if (filter === "all") list = requests;
    else if (filter === "completed") list = requests.filter(isCompletedRequest);
    else if (filter === "accepted") list = requests.filter((r) => r.status === "accepted" && !isCompletedRequest(r));
    else list = requests.filter((r) => r.status === filter);

    // Sortierung:
    // 1. Bei "pending"/Anfragen: NEUE zuerst, dann Rest chronologisch DESC (jüngste zuerst).
    // 2. In allen anderen Tabs: ausschließlich created_at DESC (jüngste zuerst).
    const ts = (r) => r.created_at ? new Date(r.created_at).getTime() : 0;
    if (filter === "pending") {
      return [...list].sort((a, b) => {
        const an = isNewRequest(a) ? 1 : 0;
        const bn = isNewRequest(b) ? 1 : 0;
        if (an !== bn) return bn - an;
        return ts(b) - ts(a);
      });
    }
    return [...list].sort((a, b) => ts(b) - ts(a));
  })();
  const pending = requests.filter((r) => r.status === "pending").length;

  // Resolve marker info for each visible request
  const center = profile && Number.isFinite(profile.service_center_lat) && Number.isFinite(profile.service_center_lng)
    ? { lat: profile.service_center_lat, lng: profile.service_center_lng }
    : null;
  const radiusKm = profile?.service_radius_km || 0;
  const markers = filtered.map((req) => {
    const coord = geo[req.event_location];
    if (!coord) return { req, coord: null, inside: null, distance: null };
    const distance = center ? haversineKm(center, coord) : null;
    const inside = distance != null ? distance <= radiusKm : null;
    return { req, coord, inside, distance };
  });

  const validUserLoc = userLocation && Number.isFinite(userLocation.lat) && Number.isFinite(userLocation.lng) ? userLocation : null;
  const mapCenter = validUserLoc || center || { lat: 51.1657, lng: 10.4515 }; // GPS > Profil-Zentrum > DE-Default
  const initialZoom = (validUserLoc || center) ? 11 : 6; // 1:~15km Standard

  return (
    <Layout>
      <div className="flex flex-col md:flex-row h-[calc(100vh-74px)] relative">
        {/* Mobile: Toggle Liste/Karte — sticky am oberen Rand */}
        <div className="md:hidden sticky top-0 z-[1100] bg-white border-b border-gray-200 px-3 py-2 flex gap-2">
          <button
            onClick={() => setMobileView("list")}
            data-testid="mobile-view-list"
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-medium transition-all ${mobileView === "list" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`}
          >
            <List size={16} /> Liste
          </button>
          <button
            onClick={() => setMobileView("map")}
            data-testid="mobile-view-map"
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-medium transition-all ${mobileView === "map" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`}
          >
            <MapIcon size={16} /> Karte
          </button>
        </div>

        {/* Left panel — Requests list */}
        <div className={`w-full md:w-[480px] bg-white border-r border-gray-200 flex flex-col shadow-sm ${mobileView === "list" ? "flex" : "hidden"} md:flex`}>
          {/* Header */}
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-1">
              <h1 className="text-xl font-bold text-gray-900">Mein Dashboard</h1>
              {newCount > 0 && (
                <button
                  onClick={() => setFilter("pending")}
                  data-testid="new-requests-badge"
                  className="flex items-center gap-1.5 bg-[#14B8A6] hover:bg-[#0D9488] text-gray-900 px-3 py-1 rounded-full text-xs font-semibold transition-colors"
                  title="Zu neuen Anfragen springen"
                >
                  <Clock size={12} />
                  {newCount} neu
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mb-3">Hallo, {user.name}</p>
            {/* Filter tabs */}
            <div ref={tabsFadeRef} className={`flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide ${tabsFadeClass}`}>
              {[
                { key: "pending", label: "Anfragen" },
                { key: "accepted", label: "Angenommen" },
                { key: "completed", label: "Erledigt" },
                { key: "rejected", label: "Abgelehnt" },
                { key: "all", label: "Alle" },
              ].map(({ key, label }) => (
                <button key={key} onClick={() => setFilter(key)}
                  data-testid={`filter-${key}`}
                  className={`whitespace-nowrap px-3 py-1.5 text-xs font-medium rounded-full transition-all ${filter === key ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Scrollable list — caps ~6 entries via max-h, scrolls inside */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="interpreter-requests-list">
            {loading ? (
              <div className="text-center py-8 text-gray-400 text-sm">Anfragen werden geladen…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10 bg-gray-50 rounded-2xl border border-gray-200">
                <div className="text-4xl mb-2">📭</div>
                <p className="text-gray-500 text-sm">Keine Anfragen in dieser Kategorie</p>
              </div>
            ) : (
              filtered.map((req, i) => {
                const completed = isCompletedRequest(req);
                const status = completed ? STATUS_CONFIG.completed : (STATUS_CONFIG[req.status] || STATUS_CONFIG.pending);
                const m = markers.find((mm) => mm.req.id === req.id);
                const isHighlight = highlightId === req.id;
                const isSlidingIn = slidingInIds.has(req.id);
                return (
                  <motion.div key={req.id}
                    initial={isSlidingIn ? { x: -300, opacity: 0 } : { opacity: 0, y: 8 }}
                    animate={{ x: 0, y: 0, opacity: 1 }}
                    transition={isSlidingIn
                      ? { type: "spring", stiffness: 220, damping: 24 }
                      : { delay: Math.min(i * 0.03, 0.3) }
                    }
                    onClick={() => {
                      if (m?.coord) {
                        setFlyTarget(m.coord);
                        setHighlightId(req.id);
                      }
                    }}
                    data-testid={`interpreter-request-${req.id}`}
                    className={`bg-white rounded-2xl border shadow-sm p-4 cursor-pointer transition-all hover:shadow-md ${isHighlight ? "border-[#14B8A6] ring-2 ring-[#14B8A6]/30" : "border-gray-200"}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <h3 className="font-bold text-gray-900 text-sm truncate">{req.client_name || "Kund:in"}</h3>
                        {isNewRequest(req) && (
                          <span data-testid={`new-marker-${req.id}`} className="shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 bg-[#14B8A6] text-gray-900 rounded">
                            Neu
                          </span>
                        )}
                        {req.is_broadcast && (
                          <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 bg-[#EEF2FF] text-[#4F46E5] rounded border border-[#4F46E5]/20">
                            Offen
                          </span>
                        )}
                        {req.double_staffing && (
                          <span data-testid={`double-marker-${req.id}`} className="shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 bg-[#EEF2FF] text-[#4F46E5] rounded border border-[#4F46E5]/20">
                            2 Dolmis
                          </span>
                        )}
                        {req.my_role === "co_interpreter" && (
                          <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded border border-amber-300">
                            Co-Einsatz
                          </span>
                        )}
                      </div>
                      <span className={`shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full border ${status.color}`}>{status.label}</span>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-gray-700 font-medium">
                        {req.event_type}
                        {req.is_multi_day && (
                          <span className="ml-1.5 inline-flex items-center text-[10px] bg-[#F0FDFA] text-[#0D9488] px-1.5 py-0.5 rounded-full border border-[#99F6E4] font-medium">
                            {req.day_entries?.length || 0} Tage
                          </span>
                        )}
                      </p>
                      {req.is_multi_day && req.event_date_to ? (
                        <p className="text-xs text-gray-600">
                          {formatGermanDate(req.event_date)} – {formatGermanDate(req.event_date_to)}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-600">
                          {formatGermanDate(req.event_date)}
                          {req.event_time && ` · ${req.event_time}${req.event_time_end ? `–${req.event_time_end}` : ""} Uhr`}
                        </p>
                      )}
                      <div className="flex items-start gap-1 text-xs text-gray-600">
                        <MapPin size={12} className="mt-0.5 shrink-0" />
                        <span className="truncate">{req.event_location}</span>
                      </div>
                      {m?.distance != null && (
                        <p className={`text-[11px] font-medium ${m.inside ? "text-[#0D9488]" : "text-[#B45309]"}`}>
                          {m.distance.toFixed(1)} km {m.inside ? "✓ im Radius" : "× außerhalb"}
                        </p>
                      )}
                    </div>

                    {/* Compact actions */}
                    <div className="flex flex-wrap items-center justify-end gap-1.5 mt-3 pt-3 border-t border-gray-100">
                      <button onClick={(e) => { e.stopPropagation(); setSelectedDeaf(req.client_id); }} data-testid={`view-deaf-profile-${req.id}`}
                        className="flex items-center gap-1 px-2 py-1 text-[11px] bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                        <User size={11} />
                        Profil
                      </button>
                      {req.status === "pending" && req.my_role === "broadcast_candidate" && (
                        req.already_applied ? (
                          <span className="flex items-center gap-1 px-2 py-1 text-[11px] bg-amber-50 text-amber-700 rounded-lg border border-amber-200">
                            <Clock size={11} />
                            Beworben — {req.applicants?.length || 0} insgesamt
                          </span>
                        ) : (
                          <button onClick={(e) => { e.stopPropagation(); handleApplyBroadcast(req.id); }} data-testid={`apply-broadcast-${req.id}`}
                            className="flex items-center gap-1 px-2 py-1 text-[11px] bg-[#4F46E5] text-white rounded-lg hover:bg-[#4338CA] transition-colors font-medium">
                            <CheckCircle size={11} />
                            Bewerben
                          </button>
                        )
                      )}
                      {req.status === "pending" && req.my_role !== "co_interpreter" && req.my_role !== "broadcast_candidate" && (
                        <>
                          <button onClick={(e) => { e.stopPropagation(); handleStatus(req.id, "accepted"); }} data-testid={`accept-btn-${req.id}`}
                            className="flex items-center gap-1 px-2 py-1 text-[11px] bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium">
                            <CheckCircle size={11} />
                            Annehmen
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleStatus(req.id, "rejected"); }} data-testid={`reject-btn-${req.id}`}
                            className="flex items-center gap-1 px-2 py-1 text-[11px] bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors border border-red-200">
                            <XCircle size={11} />
                            Ablehnen
                          </button>
                        </>
                      )}
                      {req.my_role === "co_interpreter" && req.co_interpreter_status === "invited" && (
                        <>
                          <button onClick={(e) => { e.stopPropagation(); handleCoStatus(req.id, "accepted"); }} data-testid={`co-accept-${req.id}`}
                            className="flex items-center gap-1 px-2 py-1 text-[11px] bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium">
                            <CheckCircle size={11} />
                            Co annehmen
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleCoStatus(req.id, "declined"); }} data-testid={`co-decline-${req.id}`}
                            className="flex items-center gap-1 px-2 py-1 text-[11px] bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors border border-red-200">
                            <XCircle size={11} />
                            Absagen
                          </button>
                        </>
                      )}
                      {req.status === "accepted" && req.my_role !== "co_interpreter" && (
                        <>
                          {req.double_staffing && req.co_interpreter_status === "none" && (
                            <button onClick={(e) => { e.stopPropagation(); setInviteCoFor(req); }} data-testid={`invite-co-btn-${req.id}`}
                              className="flex items-center gap-1 px-2 py-1 text-[11px] bg-[#EEF2FF] text-[#4F46E5] rounded-lg hover:bg-[#E0E7FF] transition-colors border border-[#4F46E5]/20 font-medium">
                              <User size={11} />
                              Co einladen
                            </button>
                          )}
                          {req.double_staffing && req.co_interpreter_status === "invited" && (
                            <span className="flex items-center gap-1 px-2 py-1 text-[11px] bg-amber-50 text-amber-700 rounded-lg border border-amber-200">
                              <Clock size={11} />
                              Wartet: {req.co_interpreter_name}
                            </span>
                          )}
                          {req.double_staffing && req.co_interpreter_status === "accepted" && (
                            <span className="flex items-center gap-1 px-2 py-1 text-[11px] bg-green-50 text-green-700 rounded-lg border border-green-200">
                              <CheckCircle size={11} />
                              Co: {req.co_interpreter_name}
                            </span>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); handleResendWebhook(req.id); }} data-testid={`resend-webhook-${req.id}`}
                            title="Daten erneut an IO senden (Diagnose)"
                            className="flex items-center gap-1 px-2 py-1 text-[11px] bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors border border-gray-200">
                            <RefreshCw size={11} />
                            an IO
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/chat/${req.id}`); }} data-testid={`chat-btn-${req.id}`}
                            className="flex items-center gap-1 px-2.5 py-1 text-[11px] bg-[#14B8A6] text-gray-900 rounded-lg hover:bg-[#0D9488] transition-colors font-medium">
                            <MessageCircle size={11} />
                            Chat
                          </button>
                        </>
                      )}
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>

          {/* Stats — ans Ende der Liste */}
          <div className="grid grid-cols-3 gap-2 p-3 border-t border-gray-100 bg-white">
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-2 text-center">
              <p className="text-lg font-bold text-gray-900">{requests.length}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">Gesamt</p>
            </div>
            <div className="bg-green-50 rounded-xl border border-green-200 p-2 text-center">
              <p className="text-lg font-bold text-green-700">{requests.filter((r) => r.status === "accepted").length}</p>
              <p className="text-[10px] text-green-600 uppercase tracking-wide">Angen.</p>
            </div>
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-2 text-center">
              <p className="text-lg font-bold text-amber-700">{pending}</p>
              <p className="text-[10px] text-amber-600 uppercase tracking-wide">Offen</p>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className={`flex-1 relative ${mobileView === "map" ? "block" : "hidden"} md:block`}>
          {!center && shouldShowMap && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-amber-100 text-amber-800 px-4 py-2 rounded-2xl shadow-md text-xs font-medium border border-amber-300">
              Bitte hinterlege deinen Einsatzort im Profil, damit der Radius auf der Karte erscheint.
            </div>
          )}
          {shouldShowMap && (<>
          <MapContainer
            center={[mapCenter.lat, mapCenter.lng]}
            zoom={initialZoom}
            style={{ height: "100%", width: "100%" }}
            data-testid="interpreter-map"
          >
            <FlyTo target={flyTarget} />
            <ZoomWatcher onZoom={setMapZoom} />
            <TileLayer
              url="https://tile.openstreetmap.de/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> Mitwirkende, Stil &copy; <a href="https://www.openstreetmap.de/germanstyle.html">OpenStreetMap Deutschland</a>'
              maxZoom={18}
            />
            {/* Own service radius */}
            {center && radiusKm > 0 && (
              <Circle
                center={[center.lat, center.lng]}
                radius={radiusKm * 1000}
                pathOptions={{
                  color: "#14B8A6",
                  fillColor: "#14B8A6",
                  fillOpacity: 0.12,
                  weight: 2,
                  opacity: 0.6,
                }}
              >
                <Popup>
                  <div className="p-1">
                    <p className="font-bold text-gray-900">Dein Einsatzradius</p>
                    <p className="text-xs text-gray-500">{profile?.service_city} · {radiusKm} km</p>
                  </div>
                </Popup>
              </Circle>
            )}
            {/* Request markers */}
            {markers.filter((m) => m.coord).map((m) => (
              <Marker
                key={m.req.id}
                position={[m.coord.lat, m.coord.lng]}
                icon={makeRequestIcon(!!m.inside, markerScale)}
                eventHandlers={{ click: () => setHighlightId(m.req.id) }}
              >
                <Popup>
                  <div className="p-1 min-w-[180px]">
                    <p className="font-bold text-gray-900">{m.req.client_name}</p>
                    <p className="text-xs text-gray-600">{m.req.event_type}</p>
                    <p className="text-xs text-gray-500 mt-1">{m.req.event_location}</p>
                    {m.distance != null && (
                      <p className={`text-xs font-medium mt-1 ${m.inside ? "text-[#0D9488]" : "text-[#B45309]"}`}>
                        {m.distance.toFixed(1)} km {m.inside ? "✓ im Radius" : "× außerhalb"}
                      </p>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
          {/* Center-Button: zum eigenen Standort */}
          <button
            onClick={recenterToMe}
            disabled={locating}
            data-testid="recenter-btn"
            title="Zu meinem Standort zentrieren"
            aria-label="Zu meinem Standort zentrieren"
            className="absolute top-4 right-4 z-[1000] bg-white hover:bg-gray-50 active:bg-gray-100 rounded-full shadow-lg border border-gray-200 w-11 h-11 flex items-center justify-center transition-all disabled:opacity-60"
          >
            <LocateFixed size={20} className={`text-[#0D9488] ${locating ? "animate-pulse" : ""}`} />
          </button>
          {/* Legend */}
          <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 px-4 py-3 text-xs">
            <p className="font-semibold text-gray-900 mb-2">Legende</p>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-3 h-3 rounded-full bg-[#14B8A6]" />
              <span className="text-gray-700">Im Radius</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#F59E0B]" />
              <span className="text-gray-700">Außerhalb</span>
            </div>
          </div>
          </>)}
        </div>
      </div>

      <AnimatePresence>
        {selectedDeaf && (
          <ClientProfileModal deafUserId={selectedDeaf} onClose={() => setSelectedDeaf(null)} />
        )}
      </AnimatePresence>
      {inviteCoFor && (
        <InviteCoInterpreterModal
          request={inviteCoFor}
          currentUserId={user.id}
          onClose={() => setInviteCoFor(null)}
          onInvited={loadRequests}
        />
      )}
    </Layout>
  );
}
